// GUIDED-FLOW: DE onboarding's linked editors that write the rows the DE runs on, each in a
// modal over the flow, reusing the DE screens' own editors:
// - Permissions: the seat's agent policy (the DE profile's Permissions tab).
// - Models: the role-to-model map of its template override (Setup › Standing orders & models).
// - Home channel: its job's home channel (`PUT …/job` `home_channel_id`, as the Add-DE wizard).
// Closing a modal refetches the run, so the step's count follows.
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  NativeSelect,
  NativeSelectOption,
  QuickAddDialog,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { PencilIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { useJoinableChannels } from "../de/add-de/channels";
import { jobQuery } from "../de/add-de/model";
import { DePermissionsTab } from "../de/permissions-tab";
import { DeTemplateOverrideEditor } from "../de/template-override";
import { LoadError, Loading } from "../screen-parts";
import { FormError } from "../structure/parts";
import { useFeature } from "../use-feature";
import type { FlowRunView } from "./flow-queries";

type Props = {
  api: Api;
  company: CompanyAccess;
  view: FlowRunView;
  count: number;
  onDone: () => void;
};

const NO_DE = <p className="text-faint">No digital employee holds this seat yet.</p>;

function Opener({ label, done, onOpen }: { label: string; done: boolean; onOpen: () => void }) {
  return (
    <>
      <p className={done ? "text-secondary" : "text-faint"} data-slot="linked-count">
        {done ? "Set" : "Not set"}
      </p>
      <Button variant="outline" size="xs" className="self-start" onClick={onOpen}>
        <PencilIcon data-icon="inline-start" aria-hidden="true" />
        {label}
      </Button>
    </>
  );
}

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[760px]" data-slot="flow-linked-modal">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}

export function AgentPolicyEditor({ api, company, view, count, onDone }: Props) {
  const [open, setOpen] = useState(false);
  const deId = view.de_membership_id;
  const de = useQuery({
    queryKey: ["company", company.id, "de", deId ?? "none", "flow-profile"] as const,
    queryFn: () =>
      api.client.digitalEmployees.get({ params: { co: company.id, id: deId as string } }),
    enabled: open && deId !== null,
  });
  if (!deId) return NO_DE;
  return (
    <>
      <Opener label="Set permissions" done={count > 0} onOpen={() => setOpen(true)} />
      {open ? (
        <Modal
          title="Permissions"
          onClose={() => {
            setOpen(false);
            onDone();
          }}
        >
          {de.isPending ? (
            <Loading label="Loading the permissions" rows={3} />
          ) : de.isError ? (
            <LoadError what="The digital employee" error={de.error} />
          ) : (
            <DePermissionsTab api={api} company={company} de={de.data} />
          )}
        </Modal>
      ) : null}
    </>
  );
}

export function ModelsEditor({ api, company, view, count, onDone }: Props) {
  const [open, setOpen] = useState(false);
  const deId = view.de_membership_id;
  if (!deId) return NO_DE;
  return (
    <>
      <Opener label="Set models" done={count > 0} onOpen={() => setOpen(true)} />
      {open ? (
        <Modal
          title="Models"
          onClose={() => {
            setOpen(false);
            onDone();
          }}
        >
          <DeTemplateOverrideEditor api={api} company={company} deId={deId} />
        </Modal>
      ) : null}
    </>
  );
}

export function HomeChannelEditor({ api, company, view, count, onDone }: Props) {
  const [open, setOpen] = useState(false);
  const deId = view.de_membership_id;
  if (!deId) return NO_DE;
  return (
    <>
      <Opener label="Set home channel" done={count > 0} onOpen={() => setOpen(true)} />
      {open ? (
        <HomeChannelDialog
          api={api}
          company={company}
          deId={deId}
          onClose={() => {
            setOpen(false);
            onDone();
          }}
        />
      ) : null}
    </>
  );
}

function HomeChannelDialog({
  api,
  company,
  deId,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  deId: string;
  onClose: () => void;
}) {
  const co = company.id;
  const chatOn = useFeature("chat", co).enabled;
  const { list } = useJoinableChannels(api, co, chatOn);
  const job = useQuery(jobQuery(api, co, deId));
  const [picked, setPicked] = useState<string | null>(null);
  const value = picked ?? job.data?.home_channel_id ?? "";
  const save = useMutation({
    mutationFn: () =>
      api.client.digitalEmployees.putJob({
        params: { co, id: deId },
        body: { home_channel_id: value || null },
      }),
    onSuccess: () => {
      toast.add({ title: "Home channel saved", type: "success" });
      onClose();
    },
  });
  return (
    <QuickAddDialog
      title="Home channel"
      info="Where it reports and is reached."
      submitLabel="Save"
      canSubmit={picked !== null && !job.isPending}
      pending={save.isPending}
      onSubmit={() => save.mutate()}
      onClose={onClose}
      slot="flow-home-channel"
      error={<FormError what="Saving" error={save.error} />}
    >
      <Field>
        <FieldLabel htmlFor="flow-home-channel">Channel</FieldLabel>
        <NativeSelect
          id="flow-home-channel"
          value={value}
          disabled={!chatOn || list === null}
          onChange={(e) => setPicked(e.target.value)}
        >
          <NativeSelectOption value="">None</NativeSelectOption>
          {(list ?? []).map((c) => (
            <NativeSelectOption key={c.id} value={c.id}>
              {c.label}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </Field>
    </QuickAddDialog>
  );
}
